import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { VideoCard } from '../components/VideoCard';
import { Play, ChefHat, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { trackPageview } from '../utils/tracking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_FILTERS = [
  { value: 'all', label: 'All Videos' },
  { value: 'herb-savory', label: 'Herb & Savory', color: '#2D5016' },
  { value: 'citrus-garlic', label: 'Citrus Garlic', color: '#D4540A' },
  { value: 'smoky-savory', label: 'Smoky Savory', color: '#5C3D2E' },
  { value: 'seafood-seasoning', label: 'Seafood Seasoning', color: '#1E4D6B' },
  { value: 'full-set', label: 'Full Set', color: '#2A2A2A' },
  { value: 'general', label: 'General', color: '#666666' },
];

const CookingVideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    trackPageview('cooking-videos', '/cooking-videos');
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVideos = videos.filter((video) => {
    const matchesFilter = activeFilter === 'all' || video.related_product === activeFilter;
    const matchesSearch = !searchQuery || 
      video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (video.description && video.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="cooking-videos-page">
      {/* Hero Banner */}
      <section className="relative bg-[#2A2A2A] py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #D4540A 0%, transparent 50%), radial-gradient(circle at 75% 50%, #2D5016 0%, transparent 50%)' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Play className="w-4 h-4" fill="white" />
            Video Recipes
          </div>
          <h1 className="font-['Bodoni_Moda'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight" data-testid="videos-page-title">
            Cooking Videos
          </h1>
          <p className="text-lg text-[#B8B5B0] max-w-2xl mx-auto">
            Watch how to cook with Country Bo seasonings. Real recipes, real flavor, real easy.
          </p>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="sticky top-[64px] sm:top-[80px] z-30 bg-[#FAF8F5]/95 backdrop-blur-sm border-b border-[#E5E0D8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
              <Input
                type="text"
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white border-[#E5E0D8] text-sm h-10"
                data-testid="video-search-input"
              />
            </div>

            {/* Filter Pills */}
            <div className="flex flex-wrap gap-2 flex-1">
              {PRODUCT_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setActiveFilter(filter.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    activeFilter === filter.value
                      ? 'text-white shadow-sm'
                      : 'bg-white text-[#666] border border-[#E5E0D8] hover:border-[#999]'
                  }`}
                  style={activeFilter === filter.value ? { backgroundColor: filter.color || '#D4540A' } : {}}
                  data-testid={`filter-${filter.value}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className="text-center py-20" data-testid="no-videos-message">
            <ChefHat className="w-16 h-16 text-[#ccc] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#2A2A2A] mb-2">
              {videos.length === 0 ? 'Videos Coming Soon' : 'No matching videos'}
            </h3>
            <p className="text-[#888]">
              {videos.length === 0
                ? 'We\'re working on cooking content. Check back soon!'
                : 'Try adjusting your search or filter.'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#888] mb-6" data-testid="video-count">
              {filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredVideos.map((video) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  size="default"
                  showDescription={true}
                  showProductTag={true}
                />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default CookingVideosPage;
